import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ExistingPolicyDialog({ open, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    statement: '',
    rationale: '',
    restrictions: '',
    enforcement_mechanism: '',
    relevance_scope: '',
    time_bound: ''
  });

  const addExistingPolicyMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.Policy.create({
        ...data,
        designed_by: user.email,
        designed_at: new Date().toISOString(),
        status: 'active'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      onClose();
      setFormData({
        title: '',
        statement: '',
        rationale: '',
        restrictions: '',
        enforcement_mechanism: '',
        relevance_scope: '',
        time_bound: ''
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addExistingPolicyMutation.mutate(formData);
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-existingpolicydialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Existing Policy</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Policy Title *</Label>
            <Input data-testid="issues-existingpolicydialog-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-slate-950 border-slate-800"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Policy Statement (Clear & Unambiguous) *</Label>
            <Textarea data-testid="issues-existingpolicydialog-textarea"
              value={formData.statement}
              onChange={(e) => setFormData({ ...formData, statement: e.target.value })}
              className="bg-slate-950 border-slate-800"
              rows={3}
              placeholder="State the policy clearly and unambiguously..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Rationale</Label>
            <Textarea data-testid="issues-existingpolicydialog-textarea-1"
              value={formData.rationale}
              onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
              className="bg-slate-950 border-slate-800"
              rows={2}
              placeholder="Why does this policy exist?"
            />
          </div>

          <div className="space-y-2">
            <Label>Restrictions (Painful but Necessary)</Label>
            <Textarea data-testid="issues-existingpolicydialog-textarea-2"
              value={formData.restrictions}
              onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
              className="bg-slate-950 border-slate-800"
              rows={2}
              placeholder="What restrictions does this policy impose?"
            />
          </div>

          <div className="space-y-2">
            <Label>Enforcement Mechanism</Label>
            <Textarea data-testid="issues-existingpolicydialog-textarea-3"
              value={formData.enforcement_mechanism}
              onChange={(e) => setFormData({ ...formData, enforcement_mechanism: e.target.value })}
              className="bg-slate-950 border-slate-800"
              rows={2}
              placeholder="How will this policy be enforced?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Relevance Scope</Label>
              <Input data-testid="issues-existingpolicydialog-input-1"
                value={formData.relevance_scope}
                onChange={(e) => setFormData({ ...formData, relevance_scope: e.target.value })}
                className="bg-slate-950 border-slate-800"
                placeholder="Who/where does this apply?"
              />
            </div>

            <div className="space-y-2">
              <Label>Time Bound</Label>
              <Input data-testid="issues-existingpolicydialog-input-2"
                value={formData.time_bound}
                onChange={(e) => setFormData({ ...formData, time_bound: e.target.value })}
                className="bg-slate-950 border-slate-800"
                placeholder="e.g., Review quarterly"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button data-testid="issues-cancel-btn" type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              Add Policy
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}